import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'

export function LocalizedDecimalInput({
  value,
  locale,
  min,
  max,
  maximumFractionDigits = 4,
  disabled = false,
  required = false,
  ariaLabel,
  onChange,
}: {
  value: number
  locale: string
  min?: number
  max?: number
  maximumFractionDigits?: number
  disabled?: boolean
  required?: boolean
  ariaLabel?: string
  onChange: (value: number) => void
}) {
  const format = (number: number) => new Intl.NumberFormat(locale, {
    useGrouping: false,
    maximumFractionDigits,
  }).format(number)
  const [displayValue, setDisplayValue] = useState(() => format(value))

  useEffect(() => {
    setDisplayValue(format(value))
  }, [locale, value])

  return <input
    type="text"
    inputMode="decimal"
    autoComplete="off"
    disabled={disabled}
    required={required}
    aria-label={ariaLabel}
    value={displayValue}
    onBlur={() => setDisplayValue(format(value))}
    onChange={(event) => {
      const next = event.target.value
      setDisplayValue(next)
      const parsed = parseLocalizedNumber(next, locale)
      if (
        parsed !== null &&
        (min === undefined || parsed >= min) &&
        (max === undefined || parsed <= max)
      ) {
        onChange(parsed)
      }
    }}
  />
}

export function LocalizedDateInput({
  value,
  locale,
  weekStartsOn = 1,
  required = false,
  disabled = false,
  ariaLabel,
  pickerLabel = datePickerLabels(locale).open,
  previousMonthLabel = datePickerLabels(locale).previous,
  nextMonthLabel = datePickerLabels(locale).next,
  onChange,
}: {
  value: string
  locale: string
  weekStartsOn?: 0 | 1 | 'Sunday' | 'Monday'
  required?: boolean
  disabled?: boolean
  ariaLabel?: string
  pickerLabel?: string
  previousMonthLabel?: string
  nextMonthLabel?: string
  onChange: (value: string) => void
}) {
  const [displayValue, setDisplayValue] = useState(
    () => formatIsoDate(value, locale),
  )
  const [pickerOpen, setPickerOpen] = useState(false)
  const [visibleMonth, setVisibleMonth] = useState(() =>
    value ? parseIsoDate(value) : startOfUtcDay(new Date()))
  const pickerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const popoverStyle = useAnchoredPopover(pickerOpen, pickerRef, popoverRef)

  useEffect(() => {
    setDisplayValue(formatIsoDate(value, locale))
    if (value) setVisibleMonth(parseIsoDate(value))
  }, [locale, value])

  useEffect(() => {
    if (!pickerOpen) return
    const close = (event: PointerEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node) &&
        !popoverRef.current?.contains(event.target as Node)) setPickerOpen(false)
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [pickerOpen])

  const normalizedWeekStart = weekStartsOn === 0 || weekStartsOn === 'Sunday'
    ? 0 : 1
  const year = visibleMonth.getUTCFullYear()
  const month = visibleMonth.getUTCMonth()
  const firstDay = new Date(Date.UTC(year, month, 1))
  const leadingDays = (firstDay.getUTCDay() - normalizedWeekStart + 7) % 7
  const gridStart = new Date(Date.UTC(year, month, 1 - leadingDays))
  const days = Array.from({ length: 42 }, (_, index) =>
    new Date(Date.UTC(
      gridStart.getUTCFullYear(),
      gridStart.getUTCMonth(),
      gridStart.getUTCDate() + index,
    )))
  const weekDays = Array.from({ length: 7 }, (_, index) =>
    new Date(Date.UTC(
      2023, 0, 1 + ((normalizedWeekStart + index) % 7),
    )))

  const changeMonth = (offset: number) =>
    setVisibleMonth(new Date(Date.UTC(year, month + offset, 1)))
  const labels = datePickerLabels(locale)
  const years = Array.from({ length: 301 }, (_, index) => 1900 + index)

  return <div className="localized-input-with-picker" ref={pickerRef}>
    <input type="text" inputMode="numeric" autoComplete="off"
      required={required} disabled={disabled} aria-label={ariaLabel}
      placeholder={datePlaceholder(locale)} value={displayValue}
      onBlur={() => setDisplayValue(formatIsoDate(value, locale))}
      onChange={(event) => {
        const next = event.target.value
        setDisplayValue(next)
        if (!next.trim()) {
          onChange('')
          return
        }
        const parsed = parseLocalizedDate(next, locale)
        if (parsed) onChange(parsed)
      }} />
    <button className="input-picker-button" type="button" disabled={disabled}
      aria-label={pickerLabel} aria-expanded={pickerOpen}
      onClick={() => setPickerOpen(current => !current)}>
      <span aria-hidden="true">&#128197;</span>
    </button>
    {pickerOpen && createPortal(<div ref={popoverRef}
      className="date-picker-popover anchored-picker-popover" style={popoverStyle}
      role="dialog" aria-label={pickerLabel}>
      <div className="date-picker-header">
        <button type="button" aria-label={previousMonthLabel}
          onClick={() => changeMonth(-1)}>‹</button>
        <div className="date-picker-selectors">
          <select aria-label={labels.month} value={month}
            onChange={(event) => setVisibleMonth(new Date(Date.UTC(
              year, Number(event.target.value), 1,
            )))}>
            {Array.from({ length: 12 }, (_, index) => <option key={index} value={index}>
              {new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' })
                .format(new Date(Date.UTC(2024, index, 1)))}
            </option>)}
          </select>
          <select aria-label={labels.year} value={year}
            onChange={(event) => setVisibleMonth(new Date(Date.UTC(
              Number(event.target.value), month, 1,
            )))}>
            {years.map(option => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <button type="button" aria-label={nextMonthLabel}
          onClick={() => changeMonth(1)}>›</button>
      </div>
      <div className="date-picker-grid">
        {weekDays.map(day => <span className="date-picker-weekday"
          key={day.getUTCDay()}>{new Intl.DateTimeFormat(locale, {
            weekday: 'short', timeZone: 'UTC',
          }).format(day)}</span>)}
        {days.map(day => {
          const iso = toIsoDate(day)
          const outside = day.getUTCMonth() !== month
          return <button type="button" key={iso}
            className={`${outside ? 'outside' : ''} ${iso === value ? 'selected' : ''}`}
            aria-label={new Intl.DateTimeFormat(locale, {
              dateStyle: 'full', timeZone: 'UTC',
            }).format(day)}
            onClick={() => {
              onChange(iso)
              setPickerOpen(false)
            }}>{day.getUTCDate()}</button>
        })}
      </div>
    </div>, document.body)}
  </div>
}

function datePickerLabels(locale: string) {
  const language = locale.slice(0, 2)
  return ({
    es: { open: 'Abrir calendario', previous: 'Mes anterior', next: 'Mes siguiente', month: 'Mes', year: 'Año' },
    en: { open: 'Open calendar', previous: 'Previous month', next: 'Next month', month: 'Month', year: 'Year' },
    fr: { open: 'Ouvrir le calendrier', previous: 'Mois précédent', next: 'Mois suivant', month: 'Mois', year: 'Année' },
    de: { open: 'Kalender öffnen', previous: 'Vorheriger Monat', next: 'Nächster Monat', month: 'Monat', year: 'Jahr' },
    it: { open: 'Apri calendario', previous: 'Mese precedente', next: 'Mese successivo', month: 'Mese', year: 'Anno' },
    pt: { open: 'Abrir calendário', previous: 'Mês anterior', next: 'Mês seguinte', month: 'Mês', year: 'Ano' },
    ca: { open: 'Obre el calendari', previous: 'Mes anterior', next: 'Mes següent', month: 'Mes', year: 'Any' },
  } as Record<string, { open: string; previous: string; next: string; month: string; year: string }>)[language] ??
    { open: 'Open calendar', previous: 'Previous month', next: 'Next month', month: 'Month', year: 'Year' }
}

export function LocalizedTimeInput({
  value,
  locale,
  allowEndOfDay = false,
  required = false,
  ariaLabel,
  pickerLabel,
  hourLabel,
  minuteLabel,
  dayPeriodLabel,
  onChange,
}: {
  value: number | null
  locale: string
  allowEndOfDay?: boolean
  required?: boolean
  ariaLabel: string
  pickerLabel: string
  hourLabel: string
  minuteLabel: string
  dayPeriodLabel: string
  onChange: (value: number) => void
}) {
  const [displayValue, setDisplayValue] = useState(
    () => value === null ? '' : formatMinutes(value, locale),
  )
  const [pickerOpen, setPickerOpen] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const popoverStyle = useAnchoredPopover(pickerOpen, pickerRef, popoverRef)

  useEffect(() => {
    setDisplayValue(value === null ? '' : formatMinutes(value, locale))
  }, [locale, value])

  useEffect(() => {
    if (!pickerOpen) return
    const close = (event: PointerEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        !popoverRef.current?.contains(event.target as Node)
      ) setPickerOpen(false)
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [pickerOpen])

  const normalizedValue = value === null || value === 1440 ? 0 : value
  const hour = Math.floor(normalizedValue / 60)
  const minute = normalizedValue % 60
  const hourCycle = timeFormatter(locale).resolvedOptions().hourCycle
  const usesTwelveHours = hourCycle === 'h11' || hourCycle === 'h12'
  const period = hour >= 12 ? 'pm' : 'am'
  const displayedHour = usesTwelveHours ? ((hour + 11) % 12) + 1 : hour
  const minuteOptions = [...new Set([
    0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, minute,
  ])].sort((left, right) => left - right)
  const changeTime = (
    nextDisplayedHour: number,
    nextMinute: number,
    nextPeriod: 'am' | 'pm' = period,
  ) => {
    let nextHour = nextDisplayedHour
    if (usesTwelveHours) {
      nextHour %= 12
      if (nextPeriod === 'pm') nextHour += 12
    }
    const nextValue = nextHour * 60 + nextMinute
    onChange(allowEndOfDay && nextValue === 0 ? 1440 : nextValue)
  }

  return <div className="localized-input-with-picker" ref={pickerRef}>
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      required={required}
      aria-label={ariaLabel}
      placeholder={timePlaceholder(locale)}
      value={displayValue}
      onBlur={() => setDisplayValue(value === null ? '' : formatMinutes(value, locale))}
      onChange={(event) => {
        const next = event.target.value
        setDisplayValue(next)
        const parsed = parseLocalizedTime(next, locale, allowEndOfDay)
        if (parsed !== null) onChange(parsed)
      }}
    />
    <button
      className="input-picker-button"
      type="button"
      aria-label={pickerLabel}
      aria-expanded={pickerOpen}
      onClick={() => setPickerOpen((current) => !current)}
    >
      <span aria-hidden="true">&#128336;</span>
    </button>
    {pickerOpen && createPortal(<div ref={popoverRef}
      className="time-picker-popover anchored-picker-popover"
      style={popoverStyle} role="dialog" aria-label={pickerLabel}>
      <select
        aria-label={hourLabel}
        value={displayedHour}
        onChange={(event) => changeTime(
          Number(event.target.value), minute,
        )}
      >
        {(usesTwelveHours
          ? Array.from({ length: 12 }, (_, index) => index + 1)
          : Array.from({ length: 24 }, (_, index) => index)
        ).map((option) => <option key={option} value={option}>
          {option.toString().padStart(2, '0')}
        </option>)}
      </select>
      <span aria-hidden="true">:</span>
      <select
        aria-label={minuteLabel}
        value={minute}
        onChange={(event) => changeTime(
          displayedHour, Number(event.target.value),
        )}
      >
        {minuteOptions.map((option) => <option key={option} value={option}>
          {option.toString().padStart(2, '0')}
        </option>)}
      </select>
      {usesTwelveHours && <select
        aria-label={dayPeriodLabel}
        value={period}
        onChange={(event) => changeTime(
          displayedHour,
          minute,
          event.target.value as 'am' | 'pm',
        )}
      >
        <option value="am">{dayPeriods(locale).am.toLocaleUpperCase(locale)}</option>
        <option value="pm">{dayPeriods(locale).pm.toLocaleUpperCase(locale)}</option>
      </select>}
    </div>, document.body)}
  </div>
}

export function LocalizedDateTimeInput({
  value,
  locale,
  required = false,
  dateAriaLabel,
  timeAriaLabel,
  onChange,
}: {
  value: string
  locale: string
  required?: boolean
  dateAriaLabel: string
  timeAriaLabel: string
  onChange: (value: string) => void
}) {
  const [date = '', time = '00:00'] = value.split('T')
  const timeLabels = localizedTimeLabels(locale)
  return <div className="localized-datetime-input">
    <LocalizedDateInput value={date} locale={locale} required={required}
      ariaLabel={dateAriaLabel} onChange={(next) => onChange(`${next}T${time}`)} />
    <LocalizedClockInput value={time} locale={locale} required={required}
      ariaLabel={timeAriaLabel} pickerLabel={timeLabels.open}
      hourLabel={timeLabels.hour} minuteLabel={timeLabels.minute}
      dayPeriodLabel={timeLabels.period}
      onChange={(next) => onChange(`${date}T${next}`)} />
  </div>
}

function localizedTimeLabels(locale: string) {
  const language = locale.slice(0, 2)
  return ({
    es: { open: 'Seleccionar hora', hour: 'Hora', minute: 'Minutos', period: 'Periodo del día' },
    en: { open: 'Select time', hour: 'Hour', minute: 'Minutes', period: 'Day period' },
    fr: { open: 'Sélectionner l’heure', hour: 'Heure', minute: 'Minutes', period: 'Période de la journée' },
    de: { open: 'Uhrzeit auswählen', hour: 'Stunde', minute: 'Minuten', period: 'Tageszeit' },
    it: { open: 'Seleziona ora', hour: 'Ora', minute: 'Minuti', period: 'Periodo del giorno' },
    pt: { open: 'Selecionar hora', hour: 'Hora', minute: 'Minutos', period: 'Período do dia' },
    ca: { open: 'Seleccionar hora', hour: 'Hora', minute: 'Minuts', period: 'Període del dia' },
  } as Record<string, { open: string; hour: string; minute: string; period: string }>)[language] ??
    { open: 'Select time', hour: 'Hour', minute: 'Minutes', period: 'Day period' }
}

function useAnchoredPopover(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  popoverRef: RefObject<HTMLElement | null>,
) {
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden' })
  useLayoutEffect(() => {
    if (!open) return
    const update = () => {
      const anchor = anchorRef.current
      const popover = popoverRef.current
      if (!anchor || !popover) return
      const rect = anchor.getBoundingClientRect()
      const gap = 6
      const margin = 8
      const width = popover.offsetWidth
      const height = popover.offsetHeight
      const left = Math.min(
        Math.max(margin, rect.right - width),
        window.innerWidth - width - margin,
      )
      const below = rect.bottom + gap
      const top = below + height <= window.innerHeight - margin
        ? below
        : Math.max(margin, rect.top - height - gap)
      setStyle({ left, top, visibility: 'visible' })
    }
    const frame = window.requestAnimationFrame(update)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, anchorRef, popoverRef])
  return style
}

export function LocalizedClockInput({
  value,
  locale,
  required = false,
  ariaLabel,
  pickerLabel,
  hourLabel,
  minuteLabel,
  dayPeriodLabel,
  onChange,
}: {
  value: string
  locale: string
  required?: boolean
  ariaLabel: string
  pickerLabel: string
  hourLabel: string
  minuteLabel: string
  dayPeriodLabel: string
  onChange: (value: string) => void
}) {
  const groups = value.match(/^(\d{2}):(\d{2})$/)
  const minutes = groups
    ? Number(groups[1]) * 60 + Number(groups[2])
    : null
  return <LocalizedTimeInput
    value={minutes}
    locale={locale}
    required={required}
    ariaLabel={ariaLabel}
    pickerLabel={pickerLabel}
    hourLabel={hourLabel}
    minuteLabel={minuteLabel}
    dayPeriodLabel={dayPeriodLabel}
    onChange={(next) => onChange(
      `${Math.floor(next / 60).toString().padStart(2, '0')}:${(next % 60).toString().padStart(2, '0')}`,
    )}
  />
}

export function parseLocalizedNumber(value: string, locale: string) {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6)
  const decimal = parts.find((part) => part.type === 'decimal')?.value ?? '.'
  const groups = parts
    .filter((part) => part.type === 'group')
    .map((part) => part.value)
  let normalized = value.trim().replace(/[\s\u00a0\u202f]/g, '')
  for (const group of groups) normalized = normalized.split(group).join('')
  if (decimal !== '.') normalized = normalized.replace(decimal, '.')
  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(normalized)) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function formatMinutes(value: number, locale: string) {
  const hourCycle = timeFormatter(locale).resolvedOptions().hourCycle
  if (value === 1440 && hourCycle && !['h11', 'h12'].includes(hourCycle)) {
    return '24:00'
  }
  const normalized = value === 1440 ? 0 : value
  const date = new Date(Date.UTC(
    2000, 0, 1, Math.floor(normalized / 60), normalized % 60,
  ))
  return timeFormatter(locale).format(date)
}

function parseLocalizedTime(
  value: string,
  locale: string,
  allowEndOfDay: boolean,
) {
  const hourCycle = timeFormatter(locale).resolvedOptions().hourCycle
  const usesTwelveHours = hourCycle === 'h11' || hourCycle === 'h12'
  const normalized = value.trim().toLocaleLowerCase(locale)
  const groups = normalized.match(/\d+/g)
  if (!groups || groups.length !== 2) return null
  let hours = Number(groups[0])
  const minutes = Number(groups[1])
  if (Number.isNaN(minutes) || minutes < 0 || minutes > 59) return null

  if (usesTwelveHours) {
    if (hours < 1 || hours > 12) return null
    const periods = dayPeriods(locale)
    const isPm = normalized.includes(periods.pm)
    const isAm = normalized.includes(periods.am)
    if (!isAm && !isPm) return null
    if (hours === 12) hours = 0
    if (isPm) hours += 12
    if (allowEndOfDay && isAm && hours === 0 && minutes === 0) return 1440
  } else {
    if (hours === 24 && minutes === 0 && allowEndOfDay) return 1440
    if (hours < 0 || hours > 23) return null
  }
  return hours * 60 + minutes
}

function timeFormatter(locale: string) {
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  })
}

function timePlaceholder(locale: string) {
  const hourCycle = timeFormatter(locale).resolvedOptions().hourCycle
  return hourCycle === 'h11' || hourCycle === 'h12'
    ? 'hh:mm AM/PM'
    : 'HH:mm'
}

function dayPeriods(locale: string) {
  const read = (hour: number) =>
    timeFormatter(locale)
      .formatToParts(new Date(Date.UTC(2000, 0, 1, hour)))
      .find((part) => part.type === 'dayPeriod')
      ?.value.toLocaleLowerCase(locale) ?? ''
  return { am: read(1), pm: read(13) }
}

function dateParts(locale: string) {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC',
  }).formatToParts(new Date('2006-11-22T00:00:00Z'))
}

function formatIsoDate(value: string, locale: string) {
  if (!value) return ''
  const parsed = tryParseIsoDate(value)
  if (!parsed) return ''
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC',
  }).format(parsed)
}

function datePlaceholder(locale: string) {
  return dateParts(locale).map(part => {
    if (part.type === 'day') return locale.startsWith('de') ? 'TT' : 'DD'
    if (part.type === 'month') return 'MM'
    if (part.type === 'year') {
      if (locale.startsWith('en')) return 'YYYY'
      if (locale.startsWith('de')) return 'JJJJ'
      return 'AAAA'
    }
    return part.value
  }).join('')
}

function parseLocalizedDate(value: string, locale: string) {
  const groups = value.match(/\d+/g)
  const order = dateParts(locale)
    .filter(part => ['day', 'month', 'year'].includes(part.type))
    .map(part => part.type)
  if (!groups || groups.length !== 3) return null
  const values = Object.fromEntries(order.map(
    (part, index) => [part, Number(groups[index])],
  )) as Record<'day' | 'month' | 'year', number>
  if (values.year < 1900 || values.year > 9999) return null
  const date = new Date(Date.UTC(values.year, values.month - 1, values.day))
  if (date.getUTCFullYear() !== values.year ||
    date.getUTCMonth() !== values.month - 1 ||
    date.getUTCDate() !== values.day) return null
  return toIsoDate(date)
}

function parseIsoDate(value: string) {
  return tryParseIsoDate(value) ?? startOfUtcDay(new Date())
}

function tryParseIsoDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const [year, month, day] = value.split('-').map(Number)
  const parsed = new Date(Date.UTC(year, month - 1, day))
  return parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
    ? parsed
    : null
}

function toIsoDate(value: Date) {
  return [
    value.getUTCFullYear().toString().padStart(4, '0'),
    (value.getUTCMonth() + 1).toString().padStart(2, '0'),
    value.getUTCDate().toString().padStart(2, '0'),
  ].join('-')
}

function startOfUtcDay(value: Date) {
  return new Date(Date.UTC(
    value.getFullYear(), value.getMonth(), value.getDate(),
  ))
}
